import { computed, inject, Injectable, signal } from '@angular/core';
import { UsersService } from './users.service';

export interface AccentColorOption {
  value: string;
  label: string;
  color: string;
  rgb: string;
  contrast: string;
  contrastRgb: string;
  shade: string;
  tint: string;
  soft: string;
  surface: string;
}

export const ACCENT_COLORS: AccentColorOption[] = [
  {
    value: 'blue',
    label: 'Sininen',
    color: '#036291',
    rgb: '3, 98, 145',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#035680',
    tint: '#1c729c',
    soft: 'rgba(3, 98, 145, 0.08)',
    surface: '#f2f8fb',
  },
  {
    value: 'green',
    label: 'Vihreä',
    color: '#328A50',
    rgb: '50, 138, 80',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#2c7946',
    tint: '#479662',
    soft: 'rgba(50, 138, 80, 0.08)',
    surface: '#f1faf4',
  },
  {
    value: 'orange',
    label: 'Oranssi',
    color: '#c97a22',
    rgb: '201, 122, 34',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#b36d1f',
    tint: '#d18939',
    soft: 'rgba(201, 122, 34, 0.1)',
    surface: '#fff7ed',
  },
  {
    value: 'purple',
    label: 'Violetti',
    color: '#7c3aed',
    rgb: '124, 58, 237',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#6d32d0',
    tint: '#8b4bf0',
    soft: 'rgba(124, 58, 237, 0.08)',
    surface: '#f6f2ff',
  },
  {
    value: 'red',
    label: 'Punainen',
    color: '#B82258',
    rgb: '184, 34, 88',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#a21e4d',
    tint: '#bf3869',
    soft: 'rgba(184, 34, 88, 0.08)',
    surface: '#fff1f5',
  },
  {
    value: 'teal',
    label: 'Turkoosi',
    color: '#1B7A7A',
    rgb: '27, 122, 122',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#186b6b',
    tint: '#328787',
    soft: 'rgba(27, 122, 122, 0.08)',
    surface: '#effafa',
  },
];

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private usersService = inject(UsersService);

  private currentAccentColor = signal('blue');

  private resolveAccentColor(value?: string | null): AccentColorOption {
    return ACCENT_COLORS.find((option) => option.value === value) ?? ACCENT_COLORS[0];
  }

  async setAccentColor(accentColor: string) {
    const resolved = this.resolveAccentColor(accentColor);
    this.currentAccentColor.set(resolved.value);
    return this.usersService.updateOnly(this.usersService.currentUserS().guid, { theme: resolved.value });
  }

  getAccentColor() {
    return computed(() => {
      const user = this.usersService.currentUserS();
      return this.resolveAccentColor(user?.theme ?? this.currentAccentColor()).value;
    });
  }

  getAccentConfig() {
    return computed(() => this.resolveAccentColor(this.getAccentColor()()));
  }
}
